import { useCallback, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { config } from '../config/config';
import { getEventSubscribeUrl } from '../services/iot/getEventSubscribeUrl';
import { iotClient } from '../services/iot/IotClient';
import useIdentityContext from './useIdentityContext';

export interface Subscriber {
  /**
   * Callback when event happens.
   *
   * @param message message from server
   */
  onMessage: (message: Record<string, any>) => void | Promise<void>;

  /**
   * List of commands to subscribe to.
   * Use an empty array to apply no filter.
   * i.e. ['case_created', 'case_updated']
   */
  commands: string[];
}

// The MQTT client is a singleton, so its reconnect state must be a singleton
// too. Several dashboard components call this hook; component-local timers
// would otherwise interleave and multiply requests to /events/subscribe.
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;
let latestRefreshConnection: (() => Promise<void>) | null = null;

export default function useSubscribe(): {
  subscribe: (subscriber: Subscriber) => string;
  unsubscribe: (id: string) => void;
} {
  const subId = useMemo(() => uuidv4(), []);

  const { userInfo } = useIdentityContext();
  const userId = userInfo?.id;

  const subscribe = useCallback(
    (subscriber: Subscriber): string => {
      return iotClient.onMessage(subId, (topic, message) => {
        if ('command' in message) {
          if (subscriber.commands.length === 0 || subscriber.commands.includes(message.command)) {
            subscriber.onMessage(message);
          }
        }
      });
    },
    [subId],
  );

  const unsubscribe = useCallback((): void => {
    iotClient.offMessage(subId);
  }, [subId]);

  const clearReconnectTimer = useCallback((): void => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }, []);

  const scheduleReconnect = useCallback((): void => {
    if (reconnectTimer || iotClient.connected || iotClient.connecting) return;

    const baseDelay = Math.max(config.iot.reconnectPeriod, 1000);
    const reconnectDelay = Math.min(baseDelay * 2 ** reconnectAttempt, 5 * 60 * 1000);
    reconnectAttempt += 1;

    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      if (latestRefreshConnection) void latestRefreshConnection();
    }, reconnectDelay);
  }, []);

  const refreshConnection = useCallback(async (): Promise<void> => {
    // A later-mounted component may call this hook while another subscriber
    // already has a retry scheduled. Respect that shared timer instead of
    // bypassing the backoff with a fresh immediate request.
    if (reconnectTimer || iotClient.connected || iotClient.connecting) {
      return;
    }

    iotClient.connecting = true;
    let connectionFailed = false;

    try {
      const { url } = await getEventSubscribeUrl();
      if (iotClient.hasIoT) {
        iotClient.disconnect();
      }
      iotClient.endpointURL = url;
      iotClient.connect(`${config.iot.prefix}-${userId}-${Date.now()}`);
    } catch (e) {
      // Real-time events enhance the dashboard but are not required for using
      // it. Avoid creating a new error toast on every background retry when
      // the subscribe endpoint is unavailable; the REST-backed UI continues
      // to work and the connection is retried with bounded backoff.
      console.warn('Real-time event subscription unavailable; retrying in the background.', e);
      connectionFailed = true;
    } finally {
      iotClient.connecting = false;
    }

    if (connectionFailed) scheduleReconnect();
  }, [scheduleReconnect, userId]);

  latestRefreshConnection = refreshConnection;

  useEffect(() => {
    if (userId) {
      iotClient.onConnect = () => {
        clearReconnectTimer();
        reconnectAttempt = 0;
        iotClient.subscribe(`${config.iot.prefix}/${userId}`);
      };

      iotClient.onError = (error: Error) => {
        console.error('IoT connection error', error);
        scheduleReconnect();
      };

      iotClient.onClose = () => {
        scheduleReconnect();
      };

      void refreshConnection();
    } else {
      clearReconnectTimer();
      iotClient.reset();
      iotClient.disconnect();
    }
  }, [clearReconnectTimer, refreshConnection, scheduleReconnect, userId]);

  return {
    subscribe,
    unsubscribe,
  };
}
